import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@chikara/ui';
import { t } from '@/i18n';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { SwitchCompanyModal } from '@/features/auth/components/SwitchCompanyModal';
import { trackCompanyEvent } from '@/features/auth/analytics/companyEvents';
import * as ROUTES from '@/shared/constants/routes';
import styles from './CompanySwitcher.module.css';

export interface CompanySwitcherProps {
  companyName: string;
  companyId: string;
}

export function CompanySwitcher({ companyName, companyId }: CompanySwitcherProps) {
  const navigate = useNavigate();
  const { companies, activeCompany, switchCompany } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [targetUuid, setTargetUuid] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

  const target = companies.find((company) => company.customer_uuid === targetUuid) ?? null;
  const canSwitch = Boolean(target && target.customer_uuid !== activeCompany?.customer_uuid);

  const closePopover = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return;
    const selectedIndex = companies.findIndex((company) => company.customer_uuid === activeCompany?.customer_uuid);
    optionRefs.current[Math.max(selectedIndex, 0)]?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePopover();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const togglePopover = () => {
    setTargetUuid(activeCompany?.customer_uuid ?? null);
    setIsOpen((open) => !open);
  };

  const closeConfirm = () => {
    setIsConfirming(false);
    setSwitchError(undefined);
  };

  const handleOptionKeyDown = (event: ReactKeyboardEvent<HTMLLIElement>, index: number, uuid: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setTargetUuid(uuid);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = (index + step + companies.length) % companies.length;
    optionRefs.current[next]?.focus();
  };

  const confirmSwitch = async () => {
    if (!target) return;
    setIsSwitching(true);
    setSwitchError(undefined);
    try {
      await switchCompany(target.customer_uuid);
      setIsConfirming(false);
      navigate(ROUTES.DASHBOARD, { replace: true });
    } catch {
      setSwitchError(t('companies.confirm.error'));
      trackCompanyEvent('company_switch_failed', { customer_uuid: target.customer_uuid });
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button type="button" ref={triggerRef} className={styles.trigger} aria-expanded={isOpen} aria-haspopup="listbox" onClick={togglePopover}>
        <span className={styles.triggerName}>
          {companyName}
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size="sm" />
        </span>
        <span className={styles.triggerId}>{t('companies.switcher.identifier', { id: companyId })}</span>
      </button>

      {isOpen && (
        <div className={styles.popover}>
          <div className={styles.popoverHeader}>
            <span className={styles.popoverTitle}>{t('companies.switcher.title')}</span>
            <button type="button" className={styles.close} aria-label={t('companies.switcher.close')} onClick={closePopover}>
              <Icon name="close" size="sm" />
            </button>
          </div>
          <div className={styles.body}>
            <ul className={styles.list} role="listbox" aria-label={t('companies.switcher.title')}>
              {companies.map((company, index) => {
                const isSelected = company.customer_uuid === targetUuid;
                return (
                  <li
                    key={company.customer_uuid}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={isSelected ? 0 : -1}
                    ref={(element) => {
                      optionRefs.current[index] = element;
                    }}
                    onKeyDown={(event) => handleOptionKeyDown(event, index, company.customer_uuid)}
                    className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                    onClick={() => setTargetUuid(company.customer_uuid)}
                  >
                    {company.company_name}
                    {isSelected && <Icon name="check" size="sm" />}
                  </li>
                );
              })}
            </ul>
            <Button
              variant="primary"
              className={styles.action}
              disabled={!canSwitch}
              onClick={() => {
                setIsOpen(false);
                setIsConfirming(true);
              }}
            >
              {t('companies.switcher.action')}
            </Button>
          </div>
        </div>
      )}

      <SwitchCompanyModal
        isOpen={isConfirming}
        currentCompanyName={activeCompany?.company_name ?? companyName}
        targetCompanyName={target?.company_name ?? ''}
        isLoading={isSwitching}
        errorMessage={switchError}
        onConfirm={confirmSwitch}
        onClose={closeConfirm}
      />
    </div>
  );
}
